<script setup>
    import MainHeader from '@/components/HeaderComponent.vue';
    import agreementDetail from '@/components/modal/AgreementDetailComponent.vue';
    import userLoginPasswordUpdate from '@/components/modal/UserLoginPasswordUpdateComponent.vue';
    import { ref, inject, onMounted, computed } from 'vue';
    import { useUserStore } from '@/stores/user/userStore';
    import { useAgreementDetailStore } from '@/stores/modal/agreementDetailStore';
    import { useCommonStore } from '@/stores/common/commonStore';

    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');
    // ユーザー情報取得
    const userStore = useUserStore();
    // ユーザー情報取得
    const agreementStore = useAgreementDetailStore();
    const commonStore = useCommonStore();

    // パスワード変更モーダル
    const userLoginPasswordUpdateDispFlg = ref('');
    /**
     * パスワード変更モーダル画面オープン処理
     */
    function userLoginPasswordUpdateOpen() {
        userLoginPasswordUpdateDispFlg.value = 'block';

        // パスワード更新モーダルの初期処理を実行する。
        userLoginPasswordUpdateModalComponent.value.openModal(/*API_URL*/);
    }
    const userLoginPasswordUpdateModalComponent = ref();
    /**
     * パスワード変更モーダル画面クローズ処理
     */
    function userLoginPasswordUpdateClose() {
        userLoginPasswordUpdateDispFlg.value = '';
    }
    /**
     * パスワード変更モーダル画面変更処理
     */
    function userLoginPasswordUpdateUpdate() {
        userLoginPasswordUpdateDispFlg.value = '';
    }

    // 規定詳細モーダル
    const agreementDetailDispFlg = ref('');
    const agreementDetailComponent = ref();
    /**
     * 規定詳細モーダルオープン処理
     */
    function agreementDetailOpen() {
        agreementDetailComponent.value.openModal(commonStore.API_URL, agreementStore.RegulationPk);
        agreementDetailDispFlg.value = 'block';
    }
    /**
     * 規定詳細モーダルクローズ処理
     */
    function agreementDetailClose() {
        agreementDetailDispFlg.value = '';
    }

    onMounted(async () => {
        await commonStore.getDomain();
        reset();
        // ユーザー情報取得
        await getUserInfo();
        // ユーザー同意規定取得
        await getUserAgreement();
    });

    const isPasswordDisp = computed(() => {
        return userStore.AuthenticateMethod === '1' ? true : false;
    });

    /**
     * ユーザ情報取得処理
     */
    async function getUserInfo() {
        const response = await userStore.getUserAction(commonStore.API_URL);
        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
        return;
    }
    /**
     * ユーザ同意規定取得処理
     */
    async function getUserAgreement() {
        const response = await agreementStore.getUserAgreementAction(commonStore.API_URL);
        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
        return;
    }

    /**
     * リセットする。
     */
    function reset() {
        userStore.reset();
        agreementStore.reset();
    }
</script>

<style></style>

<template>
    <section id="main">
        <section id="overlay" :style="{ display: userLoginPasswordUpdateDispFlg }">
            <userLoginPasswordUpdate ref="userLoginPasswordUpdateModalComponent" @userLoginPasswordUpdateModal:cancel="userLoginPasswordUpdateClose" @userLoginPasswordUpdateModal:update="userLoginPasswordUpdateUpdate" />
        </section>
        <!--パスワード変更-->
        <section id="overlay" :style="{ display: agreementDetailDispFlg }">
            <agreementDetail ref="agreementDetailComponent" @agreementDetailModal:close="agreementDetailClose" />
        </section>
        <!--利用規定-->
        <MainHeader />
        <div id="contents">
            <div class="whiteBox">
                <h3 class="textCenter">
                    <span>{{ $t('user.parts.pageTitle1') }}</span>
                </h3>
                <section class="whiteBoxContents">
                    <section class="form userData">
                        <article>
                            <!-- 企業ID -->
                            <dl class="left" style="margin-bottom: 15px">
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('user.parts.dt6') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <span>{{ userStore.CompanyId }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <!-- 契約企業 -->
                            <dl class="left" style="margin-bottom: 15px">
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('user.parts.dt1') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <span>{{ userStore.CompanyNm }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <!-- ユーザーID -->
                            <dl class="left" style="margin-bottom: 15px">
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('user.parts.dt7') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <span>{{ userStore.UserId }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <!-- 氏名 -->
                            <dl class="left" style="margin-bottom: 15px">
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('user.parts.dt2') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <span>{{ userStore.NameKj }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <!-- メールアドレス -->
                            <dl class="left" style="margin-bottom: 15px">
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('user.parts.dt3') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <span>{{ userStore.MailAddress }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                        </article>
                        <hr />
                        <!-- パスワード -->
                        <article v-show="isPasswordDisp">
                            <dl class="left" style="align-items: center">
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('user.parts.dt4') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput btnArea">
                                        <li style="margin-left: auto">
                                            <button type="button" class="btnPrimary" id="changePassword" @click="userLoginPasswordUpdateOpen">{{ $t('user.parts.button1') }}</button>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                        </article>
                        <hr />
                        <!--利用規定-->
                        <article>
                            <dl class="left" style="align-items: center">
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('user.parts.dt5') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput btnArea" style="display: flex; align-items: center">
                                        <li>
                                            <span>{{ agreementStore.AgreementDt }}</span>
                                        </li>
                                        <li style="margin-left: auto">
                                            <button type="button" class="btnPrimary" id="termsDetail" @click="agreementDetailOpen">{{ $t('user.parts.button2') }}</button>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                        </article>
                    </section>
                </section>
                <section class="whiteBoxFooter">
                    <ul class="btnArea center">
                        <li>
                            <router-link to="/home">
                                <button type="button" id="moveToHome" class="btnNormal">{{ $t('message.common.parts.back') }}</button>
                            </router-link>
                        </li>
                    </ul>
                </section>
            </div>
        </div>
    </section>
</template>
